import React from "react";
import { Minus, Plus } from "lucide-react";
import { fmt, fmtPiles } from "@/lib/api";

const ACCENTS = {
  amber: { ring: "border-amber-300", chip: "bg-amber-50 text-amber-800 border-amber-200", val: "text-amber-900" },
  green: { ring: "border-green-300", chip: "bg-green-50 text-green-800 border-green-200", val: "text-green-900" },
  red: { ring: "border-red-300", chip: "bg-red-50 text-red-800 border-red-200", val: "text-red-900" },
  blue: { ring: "border-blue-300", chip: "bg-blue-50 text-blue-800 border-blue-200", val: "text-blue-900" },
};

const QUICK = [10, 50, 100];

export function StepperRow({ label, unit, value, onChange, accent = "blue", max, testid, pileSize, unitPile }) {
  const a = ACCENTS[accent] || ACCENTS.blue;
  const v = Number(value) || 0;
  const set = (n) => onChange(Math.max(0, max != null ? Math.min(max, n) : n));
  const showPile = pileSize && pileSize > 1;
  const unitHelp = !showPile && unitPile && unitPile > 1;
  const quick = showPile
    ? [1, 5, 10].map((q) => ({ add: q, label: `+${q}` }))
    : unitHelp
      ? [{ add: unitPile, label: "+1 pile" }, { add: unitPile * 5, label: "+5 piles" }]
      : QUICK.map((q) => ({ add: q, label: `+${q}` }));

  return (
    <div className={`rounded-xl border-2 ${a.ring} bg-white p-3`} data-testid={testid ? `${testid}-row` : undefined}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-display font-bold text-base text-slate-800">
          {label}
          {showPile && <span className="ml-1 text-xs font-normal text-slate-500">(1 pile = {fmt(pileSize)})</span>}
          {unitHelp && <span className="ml-1 text-xs font-normal text-slate-500">(unités · 1 pile = {fmt(unitPile)})</span>}
        </span>
        {max != null && (
          <span className="text-xs font-data text-slate-500">dispo&nbsp;: {fmt(max)}{showPile ? " piles" : ""}{unitHelp ? ` (${fmtPiles(max, unitPile, true)})` : ""}</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => set(v - 1)}
          className="tactile h-14 w-14 shrink-0 rounded-xl bg-red-100 text-red-800 flex items-center justify-center active:bg-red-200"
          data-testid={testid ? `${testid}-decrease` : "stepper-decrease-button"}
          aria-label="Diminuer"
        >
          <Minus className="h-7 w-7" strokeWidth={3} />
        </button>
        <input
          type="number"
          inputMode="numeric"
          value={v === 0 ? "" : v}
          placeholder="0"
          onChange={(e) => set(parseInt(e.target.value || "0", 10))}
          className={`h-14 flex-1 min-w-0 rounded-xl border-2 ${a.ring} bg-slate-50 text-center font-data text-3xl font-black ${a.val} focus:outline-none focus:ring-4 focus:ring-blue-200`}
          data-testid={testid ? `${testid}-value` : "stepper-value-display"}
        />
        <button
          type="button"
          onClick={() => set(v + 1)}
          className="tactile h-14 w-14 shrink-0 rounded-xl bg-green-100 text-green-800 flex items-center justify-center active:bg-green-200"
          data-testid={testid ? `${testid}-increase` : "stepper-increase-button"}
          aria-label="Augmenter"
        >
          <Plus className="h-7 w-7" strokeWidth={3} />
        </button>
      </div>
      <div className="mt-2 flex gap-2">
        {quick.map((q) => (
          <button
            key={q.label}
            type="button"
            onClick={() => set(v + q.add)}
            className={`tactile flex-1 h-9 rounded-lg border text-sm font-bold font-data ${a.chip} active:opacity-80`}
            data-testid={testid ? `${testid}-quick-${q.label.replace(/[^0-9a-z]/gi, "")}` : undefined}
          >
            {q.label}
          </button>
        ))}
        {v > 0 && (
          <button
            type="button"
            onClick={() => set(0)}
            className="tactile h-9 px-3 rounded-lg border border-slate-200 bg-slate-50 text-slate-500 text-sm font-semibold"
          >
            ✕
          </button>
        )}
      </div>
      {unitHelp && v > 0 && (
        <p className="mt-2 text-xs font-data text-slate-600" data-testid={testid ? `${testid}-pile-equiv` : undefined}>= {fmtPiles(v, unitPile, true)}</p>
      )}
    </div>
  );
}

export default function QtyLinesEditor({ types, value, onChange, accent = "blue", maxMap, testid, pileMode }) {
  const set = (code, n) => onChange({ ...value, [code]: n });
  return (
    <div className="space-y-3">
      {types.map((t) => (
        <StepperRow
          key={t.code}
          label={t.label}
          unit={t.unit}
          value={value[t.code] || 0}
          onChange={(n) => set(t.code, n)}
          accent={accent}
          max={maxMap ? maxMap[t.code] : undefined}
          testid={testid ? `${testid}-${t.code}` : undefined}
          pileSize={pileMode ? t.pile_size : undefined}

        />
      ))}
    </div>
  );
}
